import type { NotificationRecord } from '../models/notification.model'

const STORAGE_KEY = 'inmeet_notifications'
/**
 * Reminder keys whose notification has been cleared. The reminder scheduler
 * fires once per key by looking for it in the list, so clearing the list would
 * re-fire every reminder still inside its window on the next tick.
 */
const CLEARED_REMINDERS_KEY = 'inmeet_notifications_cleared_reminders'
const MAX_CLEARED_REMINDERS = 500

function clearedReminders(): string[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(CLEARED_REMINDERS_KEY) || '[]')
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : []
  } catch {
    return []
  }
}

/**
 * localStorage-backed CRUD for notifications. Single-key array of records.
 * Replacing with a real backend is a one-file change.
 */
export const notificationsRepo = {
  all(): NotificationRecord[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) return []
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? (parsed as NotificationRecord[]) : []
    } catch {
      return []
    }
  },

  bulkReplace(records: NotificationRecord[]) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(records))
  },

  insert(record: NotificationRecord): NotificationRecord {
    const all = this.all()
    this.bulkReplace([record, ...all])
    return record
  },

  update(id: string, patch: Partial<NotificationRecord>): NotificationRecord | null {
    const all = this.all()
    const idx = all.findIndex((n) => n.id === id)
    if (idx === -1) return null
    const updated = { ...all[idx], ...patch }
    all[idx] = updated
    this.bulkReplace(all)
    return updated
  },

  remove(id: string): boolean {
    const all = this.all()
    const next = all.filter((n) => n.id !== id)
    if (next.length === all.length) return false
    this.bulkReplace(next)
    return true
  },

  hasReminderEmitted(reminderKey: string): boolean {
    return (
      this.all().some((n) => n.metadata?.reminderKey === reminderKey) ||
      clearedReminders().includes(reminderKey)
    )
  },

  /** Remember the reminders among `records`, which are about to be removed. */
  rememberClearedReminders(records: NotificationRecord[]) {
    const keys = records.map((n) => n.metadata?.reminderKey).filter((k): k is string => typeof k === 'string')
    if (keys.length === 0) return
    const next = [...new Set([...clearedReminders(), ...keys])].slice(-MAX_CLEARED_REMINDERS)
    try {
      localStorage.setItem(CLEARED_REMINDERS_KEY, JSON.stringify(next))
    } catch {
      // Storage full or blocked: worst case a reminder shows again.
    }
  },
}
